import { ref, computed } from "@vue/composition-api";
import { debounce } from "lodash";
import axios from "@/libs/axios";
import { fetchMainUnitsForWizard } from "@/libs/risk/fetchMainUnitsForWizard";

function filterUnitsByTenant(units, tenants) {
  if (!Array.isArray(units)) return [];
  if (!Array.isArray(tenants) || !tenants.length) {
    return units.filter((unit) => unit.router);
  }
  return units.filter(
    (unit) =>
      unit.router &&
      tenants.some((tenant) => tenant.id === unit.router.id_tenant)
  );
}

function normalizeUnitResponse(response) {
  if (!response) return null;
  if (response.data && response.data.id != null) return response.data;
  if (response.id != null) return response;
  return null;
}

export function useMainUnitWizardOptions(userData) {
  const mainUnitOptions = ref([]);
  const isLoadingUnits = ref(false);
  const currentTenants = computed(() =>
    Array.isArray(userData?.tenants) ? userData.tenants : []
  );
  const unitOptions = computed(() =>
    filterUnitsByTenant(mainUnitOptions.value, currentTenants.value)
  );

  const runSearch = async (query = "") => {
    isLoadingUnits.value = true;
    try {
      const params = query && String(query).trim() !== "" ? { q: query } : {};
      mainUnitOptions.value = await fetchMainUnitsForWizard(params);
    } catch (_) {
      mainUnitOptions.value = [];
    } finally {
      isLoadingUnits.value = false;
    }
  };

  const fetchMainUnits = () => runSearch("");

  const onUnitSearch = debounce((query, loading) => {
    Promise.resolve(runSearch(query)).finally(() => {
      if (typeof loading === "function") loading(false);
    });
  }, 450);

  const resolveUnitById = async (unitId) => {
    const fromOptions = mainUnitOptions.value.find(
      (unit) => Number(unit.id) === Number(unitId)
    );
    if (fromOptions) return fromOptions;
    try {
      const { data } = await axios.get(`v1/unit/${unitId}`, {
        params: { with: "router:id,id_tenant" },
      });
      return normalizeUnitResponse(data);
    } catch (_) {
      return null;
    }
  };

  return {
    isLoadingUnits,
    unitOptions,
    fetchMainUnits,
    onUnitSearch,
    resolveUnitById,
  };
}
